import { useEffect, useState, useMemo, useCallback } from 'react'

import _ from 'lodash'
import Countdown, { zeroPad } from 'react-countdown'

import { useLevelPlaySurface } from '@/shared/level-play'

const GRID_CLASSES = {
  2: 'grid-cols-2 grid-rows-2',
  3: 'grid-cols-2 grid-rows-3',
  4: 'grid-cols-3 grid-rows-3',
  5: 'grid-cols-3 grid-rows-4',
  6: 'grid-cols-3 grid-rows-4'
}


// Duração do "peek" inicial (cartas viradas para memorização). A mesma
// constante alimenta o timer que esconde as cartas e a contagem regressiva
// exibida — precisam ficar em sincronia por montagem do desafio.
const PEEK_MS = 6000

/**
 * Fallback de carta sem imagem cadastrada (ou que falhou ao carregar) — box
 * lilás com glifo de peça de quebra-cabeça, no mesmo espírito do fallback do
 * LevelThumb. Evita o ícone nativo de "imagem quebrada" do navegador.
 */
const MemoryCardMissingImage = ({ borderClass }) => (
  <div
    className={`flex items-center justify-center rounded-xl border-2 bg-brand-surface memory-card-face memory-card-front ${borderClass}`}
  >
    <svg
      viewBox="0 0 24 24"
      className="w-8 h-8 sm:w-10 sm:h-10 text-primary opacity-45"
      fill="currentColor"
      aria-hidden
    >
      <path d="M19.5 10.5h-1v-2a3 3 0 00-3-3h-2v-1a2.5 2.5 0 00-5 0v1h-2a3 3 0 00-3 3v1.85h1a1.9 1.9 0 010 3.8h-1V16.5a3 3 0 003 3h1.85v-1a1.9 1.9 0 013.8 0v1H16.5a3 3 0 003-3v-2h1a2.5 2.5 0 000-5z" />
    </svg>
  </div>
)

export const LevelPlayMemory = ({ activity }) => {
  const { onComplete, loseHeart } = useLevelPlaySurface()
  const { meta } = activity
  const showCards = meta?.show_cards ?? true
  const cards = meta?.cards ?? []

  const [boardData, setBoardData] = useState([])
  const [flippedCards, setFlippedCards] = useState(new Set())
  const [matchedCards, setMatchedCards] = useState(new Set())
  const [brokenImages, setBrokenImages] = useState(new Set())
  const [peekUntil, setPeekUntil] = useState(null)

  const boardCards = useMemo(() => {
    return cards.flatMap((card, index) => {
      const primaryImage = { url: card.main_image?.file?.url, id: index }
      const secondaryImage = card?.secondary_image?.file?.url
        ? { url: card.secondary_image.file.url, id: index }
        : primaryImage

      return [primaryImage, secondaryImage]
    })
  }, [cards])

  const initializeBoard = useCallback(() => {
    const shuffledCards = _.shuffle(boardCards)
    setBoardData(shuffledCards)
    setFlippedCards(
      showCards ? new Set([...Array(boardCards.length * 2).keys()]) : new Set()
    )
    setMatchedCards(new Set())
    setBrokenImages(new Set())

    if (showCards) {
      setPeekUntil(Date.now() + PEEK_MS)
      setTimeout(() => setFlippedCards(new Set()), PEEK_MS)
    } else {
      setPeekUntil(null)
    }
  }, [boardCards, showCards])

  const updateActiveCards = index => {
    if (flippedCards.has(index)) return

    const newFlipped = new Set(flippedCards)
    newFlipped.add(index)

    if (flippedCards.size === 1) {
      const [firstIdx] = [...flippedCards]
      if (boardData[firstIdx].id === boardData[index].id) {
        setMatchedCards(prev => new Set([...prev, firstIdx, index]))
      } else {
        loseHeart()

        setTimeout(() => setFlippedCards(new Set(matchedCards)), 1000)
      }
    } else if (flippedCards.size >= 2) {
      newFlipped.clear()
      newFlipped.add(index)
    }

    setFlippedCards(newFlipped)
  }

  useEffect(() => {
    if (matchedCards.size === boardCards.length) {
      onComplete({
        uid: activity.uid,
        answer: `${matchedCards.size} cartas encontradas`
      })
    }
  }, [matchedCards, boardCards])

  useEffect(() => {
    initializeBoard()
  }, [initializeBoard])

  return (
    <div className="h-full">
      {showCards && peekUntil && (
        <div className="block text-center font-bold text-xl text-gray-600 mb-5 w-full h-[25px]">
          <Countdown
            key={peekUntil}
            autoStart
            date={peekUntil}
            renderer={({ total, completed }) =>
              completed ? null : (
                <span>
                  Começando em{' '}
                  <span className="text-brand">{zeroPad(total / 1000)}</span>
                  ...
                </span>
              )
            }
          />
        </div>
      )}
      <div
        className={`grid gap-2 w-full max-w-[400px] mx-auto h-full ${GRID_CLASSES[cards.length] ?? 'grid-cols-3'}`}
      >
        {boardData.map((data, i) => {
          const flipped = flippedCards.has(i)
          const matched = matchedCards.has(i)
          const borderClass =
            flipped && !matched && flippedCards.size === 2
              ? 'border-danger'
              : matched
              ? 'border-success'
              : 'border-gray-300'

          return (
            <div
              key={i}
              className={`rounded-xl cursor-pointer bg-white top-0 left-0 w-full h-full memory-card ${flipped || matched ? 'memory-card--flipped' : ''}`}
              onClick={() => updateActiveCards(i)}
            >
              {data.url && !brokenImages.has(i) ? (
                <img
                  src={data.url}
                  alt=""
                  onError={() =>
                    setBrokenImages(prev => new Set(prev).add(i))
                  }
                  className={`object-cover rounded-xl border-2 object-center cursor-pointer bg-white memory-card-face memory-card-front ${borderClass}`}
                />
              ) : (
                <MemoryCardMissingImage borderClass={borderClass} />
              )}
              <div
                className="rounded-xl border-2 border-gray-300 memory-card-face memory-card-back"
              />
            </div>
          )
        })}
      </div>
    </div>
  )
}
